import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { getActiveToken } from '../utils/session';
import './Book.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function BookingConfirmation() {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const fromPayment = searchParams.get('paid') === 'true';
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState('');

  useEffect(() => {
    if (bookingId) fetchBooking();
    else setLoading(false);
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/booking/my-bookings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const bookings = res.data.bookings || res.data.data || [];
      const match = bookings.find(b => b._id === bookingId);
      if (match) setBooking(match);
    } catch (e) {
      console.error('Error fetching booking:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric'
  });

  const formatCurrency = (n) => `$${(n || 0).toFixed(2)}`;

  // "Pay now" goes through the invoice checkout, which charges what the
  // invoice says is owed (after credit, discounts and tax; deposit first when
  // one is configured). Paying later is equally valid — the gallery unlocks
  // when the invoice is paid.
  const payNow = async () => {
    setPaying(true);
    setPayError('');
    try {
      const res = await axios.post(`${API_URL}/payment/create-checkout-session`,
        { bookingId },
        { headers: { Authorization: `Bearer ${getActiveToken()}` } });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      setPayError('Online payment is unavailable right now. Your invoice has been emailed — you can pay from it any time.');
    } catch (e) {
      setPayError(e.response?.data?.message || 'Online payment is unavailable right now. Your invoice has been emailed — you can pay from it any time.');
    }
    setPaying(false);
  };

  const needsPayment = !!booking && !fromPayment && Number(booking.totalPrice) > 0 &&
    !['paid'].includes(String(booking.paymentStatus || '').toLowerCase());

  const statusLabel = (s) => {
    const map = { pending: 'Pending Confirmation', confirmed: 'Confirmed', completed: 'Completed' };
    return map[s] || s;
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', paddingTop: 'var(--page-top-offset)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <p>Loading your booking details...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', paddingTop: 'var(--page-top-offset)', background: '#f8fafc', padding: 'var(--page-top-offset) 1rem 4rem' }}>
      <div style={{ maxWidth: '680px', margin: '0 auto' }}>

        {/* Success Header */}
        <div style={{
          background: 'white', borderRadius: '16px', padding: '2.5rem 2rem', textAlign: 'center',
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)', marginBottom: '1.5rem',
          borderTop: '4px solid #10b981'
        }}>
          <div style={{ fontSize: '3.5rem', marginBottom: '0.75rem' }}>✅</div>
          <h1 style={{ fontSize: '1.75rem', color: '#111827', margin: '0 0 0.5rem' }}>
            {fromPayment ? 'Payment Received!' : 'Booking Confirmed!'}
          </h1>
          <p style={{ color: '#6b7280', fontSize: '1.05rem', margin: 0 }}>
            {fromPayment
              ? 'Your payment has been processed and your session is secured.'
              : 'We\'ve received your booking request and you\'ll hear from us shortly.'}
          </p>
        </div>

        {/* Booking Summary */}
        {booking && (
          <div style={{
            background: 'white', borderRadius: '12px', padding: '1.75rem 2rem',
            boxShadow: '0 1px 8px rgba(0,0,0,0.05)', marginBottom: '1.5rem'
          }}>
            <h2 style={{ fontSize: '1.15rem', color: '#111827', margin: '0 0 1.25rem', fontWeight: '700' }}>Booking Summary</h2>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem 2rem' }}>
              <div>
                <div style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#9ca3af', marginBottom: '4px' }}>Property</div>
                <div style={{ fontWeight: '600', color: '#1f2937' }}>{booking.propertyAddress}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#9ca3af', marginBottom: '4px' }}>Package</div>
                <div style={{ fontWeight: '600', color: '#1f2937', textTransform: 'capitalize' }}>{booking.package}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#9ca3af', marginBottom: '4px' }}>Date & Time</div>
                <div style={{ fontWeight: '600', color: '#1f2937' }}>{formatDate(booking.appointmentDate)}</div>
                <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>{booking.appointmentTime} MT</div>
              </div>
              <div>
                <div style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#9ca3af', marginBottom: '4px' }}>Status</div>
                <span style={{
                  display: 'inline-block', padding: '3px 12px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '600',
                  background: booking.status === 'confirmed' ? '#d1fae5' : '#fef3c7',
                  color: booking.status === 'confirmed' ? '#065f46' : '#92400e'
                }}>
                  {statusLabel(booking.status)}
                </span>
              </div>
            </div>

            {/* Pricing */}
            <div style={{ borderTop: '1px solid #f3f4f6', marginTop: '1.25rem', paddingTop: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '0.9rem', color: '#4b5563' }}>
                <span>Package</span><span>{formatCurrency(booking.packagePrice)}</span>
              </div>
              {booking.addOnsTotal > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '0.9rem', color: '#4b5563' }}>
                  <span>Add-Ons</span><span>{formatCurrency(booking.addOnsTotal)}</span>
                </div>
              )}
              {booking.travelFee > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '0.9rem', color: '#4b5563' }}>
                  <span>Travel Fee</span><span>{formatCurrency(booking.travelFee)}</span>
                </div>
              )}
              {booking.discountAmount > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '0.9rem', color: '#059669' }}>
                  <span>Discount</span><span>-{formatCurrency(booking.discountAmount)}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '8px', borderTop: '1px solid #e5e7eb', fontWeight: '700', fontSize: '1.1rem', color: '#111827' }}>
                <span>Total</span><span>{formatCurrency(booking.totalPrice)}</span>
              </div>
            </div>

            {booking.photographer && (
              <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', padding: '12px 16px', marginTop: '1.25rem' }}>
                <strong style={{ color: '#0369a1' }}>Your Photographer:</strong> {booking.photographer?.name || 'Assigned'}
              </div>
            )}
          </div>
        )}

        {/* Payment — optional now, required before delivery. */}
        {needsPayment && (
          <div style={{
            background: 'white', borderRadius: '12px', padding: '1.5rem 2rem',
            boxShadow: '0 1px 8px rgba(0,0,0,0.05)', marginBottom: '1.5rem',
            borderLeft: '4px solid #2563eb'
          }}>
            <h2 style={{ fontSize: '1.15rem', color: '#111827', margin: '0 0 0.5rem', fontWeight: '700' }}>Payment</h2>
            <p style={{ color: '#4b5563', margin: '0 0 1rem', fontSize: '0.95rem', lineHeight: 1.55 }}>
              Pay now, or any time before your photos are delivered. Your gallery unlocks as soon as the invoice is paid, and the invoice is in your email and your portal.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <button type="button" onClick={payNow} disabled={paying} style={{
                padding: '11px 26px', background: '#2563eb', color: 'white', border: 'none',
                borderRadius: '8px', fontWeight: '600', fontSize: '0.95rem', cursor: paying ? 'default' : 'pointer'
              }}>
                {paying ? 'Opening secure checkout…' : 'Pay now'}
              </button>
              <span style={{ color: '#6b7280', fontSize: '0.88rem' }}>or pay later from your invoice</span>
            </div>
            {payError && <p role="alert" style={{ color: '#991b1b', margin: '0.75rem 0 0', fontSize: '0.9rem' }}>{payError}</p>}
          </div>
        )}

        {/* Next Steps */}
        <div style={{
          background: 'white', borderRadius: '12px', padding: '1.75rem 2rem',
          boxShadow: '0 1px 8px rgba(0,0,0,0.05)', marginBottom: '1.5rem'
        }}>
          <h2 style={{ fontSize: '1.15rem', color: '#111827', margin: '0 0 1rem', fontWeight: '700' }}>What Happens Next</h2>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#d1fae5', color: '#065f46', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '0.85rem', flexShrink: 0 }}>1</div>
              <div>
                <div style={{ fontWeight: '600', color: '#1f2937' }}>Confirmation Email</div>
                <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>Check your inbox for a booking confirmation with all the details.</div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#dbeafe', color: '#1d4ed8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '0.85rem', flexShrink: 0 }}>2</div>
              <div>
                <div style={{ fontWeight: '600', color: '#1f2937' }}>Fill Out Property Details</div>
                <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>Help your photographer prepare by sharing property features, access instructions, and shoot preferences.</div>
                {booking && (
                  <Link to={`/property-info/${booking._id}${booking.propertyInfoToken ? `?token=${booking.propertyInfoToken}` : ''}`} style={{
                    display: 'inline-block', marginTop: '8px', padding: '8px 20px',
                    background: '#2563eb', color: 'white', borderRadius: '6px',
                    textDecoration: 'none', fontWeight: '600', fontSize: '0.85rem'
                  }}>
                    Fill Out Property Form →
                  </Link>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#fef3c7', color: '#92400e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '0.85rem', flexShrink: 0 }}>3</div>
              <div>
                <div style={{ fontWeight: '600', color: '#1f2937' }}>Prepare the Property</div>
                <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>Declutter, turn on all lights, open blinds, make beds, and remove personal items and vehicles from the driveway.</div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#f3e8ff', color: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '0.85rem', flexShrink: 0 }}>4</div>
              <div>
                <div style={{ fontWeight: '600', color: '#1f2937' }}>Photo Delivery</div>
                <div style={{ color: '#6b7280', fontSize: '0.9rem' }}>Your edited photos will be delivered to your client portal within 24-48 hours of the shoot.</div>
              </div>
            </div>
          </div>
        </div>

        {/* Prepaid credit. Replaces the retired subscription upsell — that
            product is hidden, not deleted (HANDOFF standing reminders). */}
        <div style={{
          background: 'linear-gradient(135deg, #fef9c3 0%, #fef3c7 100%)',
          border: '2px solid #f59e0b', borderRadius: '12px',
          padding: '20px 24px', marginBottom: '1.5rem'
        }}>
          <h3 style={{ color: '#78350f', margin: '0 0 8px', fontSize: '1.1rem' }}>Booking more shoots this year?</h3>
          <p style={{ color: '#92400e', margin: '0 0 14px', fontSize: '0.95rem' }}>
            Prepaid credit packs add bonus credit on top of what you put in. It never expires and works on every package and add-on.
          </p>
          <Link to="/credit" style={{
            display: 'inline-block', padding: '10px 22px', background: '#d97706',
            color: 'white', borderRadius: '6px', textDecoration: 'none',
            fontWeight: '600', fontSize: '14px'
          }}>
            See credit packs
          </Link>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/client/portal" style={{
            padding: '12px 28px', background: '#2563eb', color: 'white', borderRadius: '8px',
            textDecoration: 'none', fontWeight: '600', fontSize: '0.95rem'
          }}>
            Go to My Portal
          </Link>
          <Link to="/book" style={{
            padding: '12px 28px', background: 'white', color: '#2563eb', borderRadius: '8px',
            textDecoration: 'none', fontWeight: '600', fontSize: '0.95rem', border: '1px solid #2563eb'
          }}>
            Book Another Session
          </Link>
        </div>
      </div>
    </div>
  );
}

export default BookingConfirmation;
